/**
 * InstallerExecution route — /installer-execution/:jobId
 *
 * P12.2 — execution-first surface for installer + subcontractor roles.
 * Renders the canonical InstallerExecutionView. Observer Mode banner +
 * lockdown fieldset preserved (Act-As toggles writes).
 */

import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useObserverMode } from "@/hooks/useObserverMode";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { ObserverBanner } from "@/components/observerMode/ObserverBanner";
import { InstallerExecutionView } from "@/components/execution/InstallerExecutionView";
import { ContractorPortalErrorBoundary } from "@/components/contractor/ContractorPortalErrorBoundary";
import { getPortalSpec } from "@/data/portal/contractorPortalSelectors";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronLeft } from "lucide-react";

function InstallerExecutionInner() {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const { jobs, contractors } = useAppData();
  const observer = useObserverMode("contractor");
  const { displayName, isObserved } = useCurrentContractor();
  const isObserverMode = observer.isObserverMode && isObserved === true;

  const job = useMemo(() => jobs.find((j) => j.id === jobId), [jobs, jobId]);
  const spec = job ? getPortalSpec(job) : undefined;

  return (
    <div
      data-testid="installer-execution-route"
      data-job-id={jobId}
      className="min-h-screen bg-background"
    >
      {(isObserverMode || observer.isSuperAdmin) && (
        <ObserverBanner
          displayName={displayName}
          onExit={observer.exit}
          pilotSwitchOnly={!isObserverMode}
          currentKind="contractor"
          currentId={observer.observedId ?? ""}
          contractorOptions={contractors.map((c) => ({
            id: c.id,
            label: c.companyName,
          }))}
          crewOptions={Array.from(
            new Set(
              jobs
                .filter(
                  (j) =>
                    j.assignmentType === "Internal Crew" && j.assignedCrew,
                )
                .map((j) => j.assignedCrew as string),
            ),
          )
            .sort()
            .map((name) => ({ id: name, label: name }))}
          isActingAs={observer.isActingAs}
          actAsRemainingMs={observer.actAsRemainingMs}
          onStartActAs={observer.startActAs}
          onEndActAs={observer.endActAs}
        />
      )}
      <fieldset
        data-testid="observer-lockdown"
        data-observer-mode={isObserverMode ? "true" : "false"}
        disabled={isObserverMode && !observer.isActingAs}
        className="contents"
      >
        <main className="max-w-md mx-auto px-4 py-4 pb-24">
          {!job ? (
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <AlertTriangle className="h-8 w-8 text-warning mx-auto" />
                <p className="text-sm font-semibold">Job not found</p>
                <p className="text-xs text-muted-foreground">
                  This job no longer exists or isn't visible to you.
                </p>
                <Link to="/contractor-portal?tab=jobs">
                  <Button variant="outline" size="sm" className="mt-2">
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to jobs
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : !spec ? (
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <AlertTriangle className="h-8 w-8 text-warning mx-auto" />
                <p className="text-sm font-semibold">No JobSpec bound</p>
                <p className="text-xs text-muted-foreground">
                  This job has no execution spec attached. Contact your PM.
                </p>
                <Link to="/contractor-portal?tab=jobs">
                  <Button variant="outline" size="sm" className="mt-2">
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to jobs
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <InstallerExecutionView job={job} spec={spec} />
          )}
        </main>
      </fieldset>
    </div>
  );
}

export default function InstallerExecution() {
  return (
    <ContractorPortalErrorBoundary>
      <InstallerExecutionInner />
    </ContractorPortalErrorBoundary>
  );
}
